import { PhoneNumberUtil } from "google-libphonenumber";

const phoneUtil = PhoneNumberUtil.getInstance();

/**
 * Detects country code from phone number format
 * @param phone - Phone number string
 * @returns ISO country code
 */
const detectCountryCode = (phone: string): string => {
  if (!phone) return "US";

  const trimmed = phone.trim();

  // International format with + or 00 - use google-libphonenumber to detect country
  if (trimmed.startsWith("+") || trimmed.startsWith("00")) {
    try {
      // Normalize 00 IDD prefix to + so ZZ region can parse it correctly
      const normalized = trimmed.startsWith("00")
        ? "+" + trimmed.slice(2)
        : trimmed;
      const parsed = phoneUtil.parseAndKeepRawInput(normalized, "ZZ");
      const region = phoneUtil.getRegionCodeForNumber(parsed);

      if (region) return region;
    } catch {
      // fall through to domestic fallbacks
    }
  }

  // UK domestic format
  if (
    trimmed.startsWith("07") ||
    trimmed.startsWith("020") ||
    trimmed.startsWith("011")
  ) {
    return "GB";
  }

  // US domestic format
  if (/^\d{10}$|^\(\d{3}\)|\d{3}-\d{3}-\d{4}/.test(trimmed)) {
    return "US";
  }

  return "US"; // fallback
};

/**
 * Validates if a phone number is valid for a given country code
 * @param phone - Phone number string to validate
 * @param countryCode - ISO country code (optional, auto-detected if not provided)
 * @returns true if valid, false otherwise
 */
export const isValidPhoneNumber = (
  phone: string,
  countryCode?: string
): boolean => {
  if (!phone) {
    return false;
  }

  try {
    const code = countryCode || detectCountryCode(phone);
    const parsed = phoneUtil.parse(phone, code);

    return phoneUtil.isValidNumber(parsed);
  } catch {
    return false;
  }
};

/**
 * Sanitizes phone number for tel: link by removing all characters except digits and +
 * @param phoneNumber - Phone number string to sanitize
 * @returns Sanitized phone number (e.g., "+12025551234")
 */
export const sanitizePhoneNumberForTel = (phoneNumber: string): string => {
  return phoneNumber.replace(/[^\d+]/g, "");
};

/**
 * Parses multiple phone numbers from a string separated by commas, semicolons, or slashes
 * @param phoneNumbers - Phone numbers string (e.g., "020 123 123 333, 022 2222 3333; 00000 00000000 / 00000 000000 . 00000 000000 \\ 00000 000000")
 * @returns Array of objects with phone number and validation status
 */
export const parsePhoneNumberList = (
  phoneNumbers: string
): Array<{ number: string; isValid: boolean }> => {
  return phoneNumbers
    .split(/[,.;/\\]/)
    .map((num) => num.trim())
    .filter((num) => num.length > 0)
    .map((num) => ({
      number: num,
      isValid: isValidPhoneNumber(num),
    }));
};
